import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Bot, User, Calculator, BarChart3, Image } from 'lucide-react';
import ChatTable from './ChatTable';

const ExpandableChatMessage = ({ message, isUser = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState('');

  const maxLength = 300;
  const shouldTruncate = message.content && message.content.length > maxLength;
  const displayContent = shouldTruncate && !isExpanded 
    ? message.content.substring(0, maxLength) + '...' 
    : message.content;

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(message.content);
    setCopiedMessage('Copied!');
    setTimeout(() => setCopiedMessage(''), 2000);
  };

  const getActionIcon = () => {
    switch (message.action) {
      case 'TABLE_REQUEST':
        return <BarChart3 className="w-4 h-4 text-blue-400" />;
      case 'REAL_ESTATE_CALCULATION':
        return <Calculator className="w-4 h-4 text-green-400" />;
      case 'IMAGE_ANALYSIS':
        return <Image className="w-4 h-4 text-purple-400" />;
      case 'MARKET_ANALYSIS':
        return <BarChart3 className="w-4 h-4 text-orange-400" />;
      default:
        return <Bot className="w-4 h-4 text-blue-400" />;
    }
  };

  const getActionLabel = () => {
    switch (message.action) {
      case 'TABLE_REQUEST':
        return 'Table Generated';
      case 'REAL_ESTATE_CALCULATION':
        return 'Calculation Complete';
      case 'IMAGE_ANALYSIS':
        return 'Image Analysis';
      case 'MARKET_ANALYSIS':
        return 'Market Analysis';
      case 'PROPERTY_ANALYSIS':
        return 'Property Search';
      case 'WEB_SEARCH':
        return 'Web Search';
      default:
        return 'AI Assistant';
    }
  };

  return (
    <div className={`flex gap-3 p-4 rounded-lg ${
      isUser 
        ? 'bg-blue-900/20 border-l-2 border-blue-500' 
        : 'bg-gray-800/50 border-l-2 border-gray-600'
    }`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-blue-600' : 'bg-gray-700'
      }`}>
        {isUser ? <User className="w-4 h-4 text-white" /> : getActionIcon()}
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        {/* Message Header */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-gray-300">
            {isUser ? 'You' : getActionLabel()}
          </span>
          {message.timestamp && (
            <span className="text-xs text-gray-500">
              {new Date(message.timestamp).toLocaleTimeString()}
            </span>
          )}
          {message.confidence && (
            <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
              {Math.round(message.confidence * 100)}% confidence
            </span>
          )}
        </div>

        {/* Text Content */}
        {message.content && (
          <div className="text-gray-200 text-sm leading-relaxed mb-3">
            <div 
              className="prose prose-invert prose-sm max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: displayContent.replace(/\n/g, '<br>')
              }}
            />
            
            {shouldTruncate && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-blue-400 hover:text-blue-300 text-sm mt-2 flex items-center gap-1"
              >
                {isExpanded ? (
                  <>Show less <ChevronUp className="w-4 h-4" /></>
                ) : (
                  <>Show more <ChevronDown className="w-4 h-4" /></>
                )}
              </button>
            )}
          </div>
        )}

        {/* Table Data */}
        {message.tableData && (
          <ChatTable 
            tableData={message.tableData} 
            isExpanded={false}
          />
        )}

        {/* Calculation Results */}
        {message.calculationResult && (
          <div className="bg-green-900/20 border border-green-700 rounded-lg p-3 mb-3">
            <h5 className="text-green-400 text-sm font-medium mb-2 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Calculation Results
            </h5>
            {message.calculationResult.tableData && (
              <ChatTable 
                tableData={message.calculationResult.tableData} 
                isExpanded={false}
              />
            )}
            {message.calculationResult.keyMetrics && (
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(message.calculationResult.keyMetrics).map(([key, value]) => (
                  <span key={key} className="bg-green-800 text-green-100 px-2 py-1 rounded text-xs">
                    {key.toUpperCase()}: {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Image Analysis */}
        {message.imageAnalysis && (
          <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-3 mb-3">
            <h5 className="text-purple-400 text-sm font-medium mb-2 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Image Analysis ({message.imageAnalysis.imageCount} images)
            </h5>
            <div className="text-gray-300 text-sm">
              {message.imageAnalysis.analysis}
            </div>
          </div>
        )}

        {/* Property Search Trigger */}
        {message.shouldTriggerPropertySearch && message.address && (
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <Bot className="w-4 h-4" />
              Property search initiated for: <span className="font-medium">{message.address}</span>
            </div>
          </div>
        )}

        {/* Message Actions */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleCopyMessage}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
          >
            <Copy className="w-3 h-3" />
            {copiedMessage || 'Copy'}
          </button>
          
          {message.webSearchResults && (
            <span className="text-xs text-gray-500">
              • {message.webSearchResults.results?.length || 0} web results
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandableChatMessage;